/**
 * SettingsTab (Tab 10) — Brokerage profile & settings.
 *
 * Section-card layout (no split panel). Groups: Brokerage Profile /
 * Branding / Integrations / Notifications / Danger Zone.
 *
 * Backend: GET  /api/broker/company-profile (existing)
 *          POST /api/broker/company-profile (existing)
 */

import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faPalette,
  faPlug,
  faBell,
  faTriangleExclamation,
  faCheckCircle,
  faFloppyDisk,
} from '@fortawesome/free-solid-svg-icons';

import axiosInstance from '../../../helpers/api';
import { colors, fonts, GoldButton, OutlineButton, NavyInput } from '../shared';

interface CompanyProfile {
  brokerage_id?: string;
  company_name?: string;
  company_address?: string;
  company_phone?: string;
  company_email?: string;
  license_number?: string;
  mls_id?: string;
  office_short_id?: string;
  association_id?: string;
  mls_login_id?: string;
  mls_name?: string;
  logo_url?: string;
  // KOPS-513 new fields
  broker_mls_id?: string;
  designated_broker?: string;
  spark_office_id?: string;
  board_name?: string;
  mls_association_id?: number | null;
}

interface MLSAssociation {
  id: number;
  association_name: string;
  short_name: string | null;
  api_provider: string | null;
  market_name: string | null;
  state: string | null;
}

interface MLSLookupResult {
  found: boolean;
  office?: {
    spark_id: string;
    name: string;
    short_id: string;
    license_number: string | null;
    board: string | null;
    mls: string;
    active: boolean;
    member_count: number;
  };
  message?: string;
}

interface SettingsTabProps {
  brokerageId?: string;
  data?: any;
}

const SettingsTab: React.FC<SettingsTabProps> = ({ brokerageId }) => {
  const [profile, setProfile] = useState<CompanyProfile>({});
  const [original, setOriginal] = useState<CompanyProfile>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // KOPS-513 — MLS associations dropdown + lookup
  const [associations, setAssociations] = useState<MLSAssociation[]>([]);
  const [lookupResult, setLookupResult] = useState<MLSLookupResult | null>(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Fetch active MLS associations for dropdown
  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get('/broker/associations/active');
        if (res.data?.success) setAssociations(res.data.associations || []);
      } catch { /* silent — dropdown stays empty */ }
    })();
  }, []);

  const handleMLSLookup = async () => {
    const shortId = profile.office_short_id?.trim();
    if (!shortId) {
      setError('Enter the Office Short ID first');
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLookingUp(true);
    setLookupResult(null);
    try {
      const res = await axiosInstance.get(`/broker/mls-lookup/office/${shortId}`);
      if (res.data?.success) {
        setLookupResult(res.data);
      } else {
        setError(res.data?.error || 'Lookup failed');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'MLS lookup failed');
    } finally {
      setLookingUp(false);
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleApplyProfile = () => {
    if (!lookupResult?.office) return;
    const o = lookupResult.office;
    setProfile((prev) => ({
      ...prev,
      company_name: o.name || prev.company_name,
      license_number: o.license_number || prev.license_number,
      board_name: o.board || prev.board_name,
      spark_office_id: o.spark_id || prev.spark_office_id,
      mls_name: o.mls || prev.mls_name,
    }));
    setSuccessMessage('✅ Profile fields updated from MLS — click Save to persist');
    setTimeout(() => setSuccessMessage(null), 4000);
  };

  const handleSyncMembers = async () => {
    if (!lookupResult?.office || !brokerageId) return;
    setSyncing(true);
    try {
      // First get members
      const membersRes = await axiosInstance.get(`/broker/mls-lookup/office/${profile.office_short_id}/members`);
      if (!membersRes.data?.success || !membersRes.data?.members?.length) {
        setError('No members found to import');
        setSyncing(false);
        return;
      }
      // Then sync
      const syncRes = await axiosInstance.post(`/broker/mls-sync/${brokerageId}`, {
        sync_profile: true,
        sync_members: true,
        office_short_id: profile.office_short_id,
        office_data: lookupResult.office,
        members_data: membersRes.data.members,
      });
      if (syncRes.data?.success) {
        const m = syncRes.data.members;
        setSuccessMessage(
          `✅ Synced! ${m.found} found · ${m.added} added · ${m.updated} updated · ${m.skipped} skipped`
        );
      } else {
        setError(syncRes.data?.error || 'Sync failed');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Sync failed');
    } finally {
      setSyncing(false);
      setTimeout(() => { setSuccessMessage(null); setError(null); }, 6000);
    }
  };

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/broker/company-profile');
      if (res.data?.success && res.data?.data) {
        const data = res.data.data;
        // Normalize potentially-nested shape
        const flat: CompanyProfile = {
          brokerage_id: data.brokerage_id,
          company_name: data.company_name || data.brokerage_name || '',
          company_address: data.company_address || data.company_data?.company_address || '',
          company_phone: data.company_phone || data.company_data?.company_phone || '',
          company_email: data.company_email || data.company_data?.company_email || '',
          license_number: data.license_number || data.company_data?.license_number || '',
          mls_id: data.mls_id || data.mls_data?.mls_id || '',
          office_short_id: data.office_short_id || data.company_data?.office_short_id || '',
          association_id: data.association_id || data.mls_data?.association_id || '',
          mls_login_id: data.mls_login_id || data.mls_data?.mls_login_id || '',
          mls_name: data.mls_name || '',
          logo_url: data.logo_url || '',
          // KOPS-513 fields
          broker_mls_id: data.broker_mls_id || '',
          designated_broker: data.designated_broker || '',
          spark_office_id: data.spark_office_id || '',
          board_name: data.board_name || '',
          mls_association_id: data.mls_association_id || null,
        };
        setProfile(flat);
        setOriginal(flat);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const hasChanges = JSON.stringify(profile) !== JSON.stringify(original);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await axiosInstance.post('/broker/company-profile', profile);
      setOriginal(profile);
      setSuccessMessage('✅ Profile saved');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setProfile(original);
    setError(null);
  };

  return (
    <div style={{ flex: 1, background: colors.offBg, overflowY: 'auto' }}>
      <div style={{ padding: '28px 32px', maxWidth: '900px', margin: '0 auto' }}>
        <HeaderCard />

        {successMessage && <Alert variant="success">{successMessage}</Alert>}
        {error && <Alert variant="danger">{error}</Alert>}

        <SectionCard icon={faBuilding} title="Brokerage Profile">
          {loading ? (
            <div style={{ padding: '20px', color: colors.muted, fontSize: '12px' }}>Loading…</div>
          ) : (
            <div style={{ display: 'grid', gap: '14px' }}>
              <Field label="Company Name">
                <WhiteInput
                  value={profile.company_name || ''}
                  onChange={(e) => setProfile({ ...profile, company_name: e.target.value })}
                  placeholder="KairosIQ AI Realty"
                />
              </Field>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <Field label="Email">
                  <WhiteInput
                    type="email"
                    value={profile.company_email || ''}
                    onChange={(e) => setProfile({ ...profile, company_email: e.target.value })}
                    placeholder="broker@brokerage.com"
                  />
                </Field>
                <Field label="Phone">
                  <WhiteInput
                    value={profile.company_phone || ''}
                    onChange={(e) => setProfile({ ...profile, company_phone: e.target.value })}
                    placeholder="(623) 552-5314"
                  />
                </Field>
              </div>
              <Field label="Office Address">
                <WhiteInput
                  value={profile.company_address || ''}
                  onChange={(e) => setProfile({ ...profile, company_address: e.target.value })}
                  placeholder="3317 S Higley Rd, Ste 114-421, Gilbert, AZ 85297"
                />
              </Field>
              <Field label="Broker License #">
                <WhiteInput
                  value={profile.license_number || ''}
                  onChange={(e) => setProfile({ ...profile, license_number: e.target.value })}
                  placeholder="BR-123456"
                />
              </Field>

              {hasChanges && (
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', paddingTop: '8px' }}>
                  <OutlineButton onClick={handleReset} disabled={saving}>
                    Reset
                  </OutlineButton>
                  <GoldButton onClick={handleSave} disabled={saving}>
                    <FontAwesomeIcon icon={faFloppyDisk} /> {saving ? 'Saving…' : 'Save Changes'}
                  </GoldButton>
                </div>
              )}
            </div>
          )}
        </SectionCard>

        <SectionCard icon={faPlug} title="MLS Configuration">
          {loading ? (
            <div style={{ padding: '20px', color: colors.muted, fontSize: '12px' }}>Loading…</div>
          ) : (
            <div style={{ display: 'grid', gap: '14px' }}>
              {/* MLS Board dropdown — only active associations */}
              <Field label="MLS Board">
                <select
                  value={profile.mls_association_id || ''}
                  onChange={(e) => {
                    const id = e.target.value ? Number(e.target.value) : null;
                    const assoc = associations.find((a) => a.id === id);
                    setProfile({
                      ...profile,
                      mls_association_id: id,
                      mls_name: assoc?.short_name || profile.mls_name || '',
                    });
                  }}
                  style={{
                    width: '100%',
                    background: colors.off,
                    color: colors.navy,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    padding: '10px 12px',
                    fontSize: '13px',
                    fontFamily: 'inherit',
                  }}
                >
                  <option value="">Select MLS Board…</option>
                  {associations.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.short_name || a.association_name} — {a.market_name || a.state || ''}
                    </option>
                  ))}
                </select>
              </Field>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <Field label="Broker MLS Member ID">
                  <WhiteInput
                    value={profile.broker_mls_id || ''}
                    onChange={(e) => setProfile({ ...profile, broker_mls_id: e.target.value })}
                    placeholder="e.g. mb678"
                  />
                </Field>
                <Field label="Office Short ID">
                  <WhiteInput
                    value={profile.office_short_id || ''}
                    onChange={(e) => setProfile({ ...profile, office_short_id: e.target.value })}
                    placeholder="e.g. kaai001"
                  />
                </Field>
              </div>

              <Field label="Designated Broker">
                <WhiteInput
                  value={profile.designated_broker || ''}
                  onChange={(e) => setProfile({ ...profile, designated_broker: e.target.value })}
                  placeholder="e.g. Michael Boykin"
                />
              </Field>

              {/* Look Up button */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <GoldButton
                  onClick={handleMLSLookup}
                  disabled={lookingUp || !profile.office_short_id?.trim()}
                  style={{ fontSize: '12px', padding: '10px 20px' }}
                >
                  {lookingUp ? 'Looking up…' : '🔍 Look Up from MLS'}
                </GoldButton>
                {profile.office_short_id && (
                  <span style={{ fontSize: '11px', color: colors.muted }}>
                    Queries ARMLS Spark API for office details + agent roster
                  </span>
                )}
              </div>

              {/* Lookup result card */}
              {lookupResult?.found && lookupResult.office && (
                <div
                  style={{
                    background: `${colors.green}08`,
                    border: `1px solid ${colors.green}44`,
                    borderLeft: `4px solid ${colors.green}`,
                    borderRadius: '8px',
                    padding: '16px 18px',
                  }}
                >
                  <div style={{ fontSize: '14px', fontWeight: 700, color: colors.navy, marginBottom: '6px' }}>
                    ✅ {lookupResult.office.name}
                  </div>
                  <div style={{ fontSize: '12px', color: '#555', lineHeight: 1.7 }}>
                    {lookupResult.office.license_number && (
                      <div>License: <strong>{lookupResult.office.license_number}</strong></div>
                    )}
                    {lookupResult.office.board && (
                      <div>Board: <strong>{lookupResult.office.board}</strong></div>
                    )}
                    <div>MLS: <strong>{lookupResult.office.mls}</strong></div>
                    <div>Agents on roster: <strong>{lookupResult.office.member_count}</strong></div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                    <GoldButton onClick={handleApplyProfile} style={{ fontSize: '12px' }}>
                      Apply to Profile
                    </GoldButton>
                    <OutlineButton onClick={handleSyncMembers} disabled={syncing} style={{ fontSize: '12px' }}>
                      {syncing ? 'Importing…' : `Import ${lookupResult.office.member_count} Agent${lookupResult.office.member_count !== 1 ? 's' : ''}`}
                    </OutlineButton>
                  </div>
                </div>
              )}

              {lookupResult && !lookupResult.found && (
                <div
                  style={{
                    background: `${colors.red}08`,
                    border: `1px solid ${colors.red}44`,
                    borderLeft: `4px solid ${colors.red}`,
                    borderRadius: '8px',
                    padding: '12px 16px',
                    fontSize: '12px',
                    color: colors.red,
                  }}
                >
                  {lookupResult.message || 'Office not found in MLS'}
                </div>
              )}

              {/* Additional MLS fields */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px' }}>
                <Field label="Board / Association">
                  <WhiteInput
                    value={profile.board_name || ''}
                    onChange={(e) => setProfile({ ...profile, board_name: e.target.value })}
                    placeholder="WeSERV"
                  />
                </Field>
                <Field label="MLS Login ID">
                  <WhiteInput
                    value={profile.mls_login_id || ''}
                    onChange={(e) => setProfile({ ...profile, mls_login_id: e.target.value })}
                    placeholder="broker_login"
                  />
                </Field>
                <Field label="Office MLS ID">
                  <WhiteInput
                    value={profile.mls_id || ''}
                    onChange={(e) => setProfile({ ...profile, mls_id: e.target.value })}
                    placeholder="kaai001"
                  />
                </Field>
              </div>

              {hasChanges && (
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', paddingTop: '8px' }}>
                  <OutlineButton onClick={handleReset} disabled={saving}>
                    Reset
                  </OutlineButton>
                  <GoldButton onClick={handleSave} disabled={saving}>
                    <FontAwesomeIcon icon={faFloppyDisk} /> {saving ? 'Saving…' : 'Save Changes'}
                  </GoldButton>
                </div>
              )}
            </div>
          )}
        </SectionCard>

        <SectionCard icon={faPalette} title="Branding">
          <div style={{ display: 'grid', gap: '14px' }}>
            <div>
              <Field label="Brokerage Logo">
                {profile.logo_url ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginTop: '6px' }}>
                    <img
                      src={profile.logo_url}
                      alt="Brokerage logo"
                      style={{
                        maxHeight: '60px',
                        maxWidth: '200px',
                        borderRadius: '6px',
                        border: `1px solid ${colors.border}`,
                        objectFit: 'contain',
                      }}
                    />
                    <OutlineButton
                      onClick={() => setProfile({ ...profile, logo_url: '' })}
                      style={{ fontSize: '11px' }}
                    >
                      Remove
                    </OutlineButton>
                  </div>
                ) : (
                  <div
                    style={{
                      marginTop: '6px',
                      padding: '20px',
                      background: colors.offBg,
                      border: `2px dashed ${colors.border}`,
                      borderRadius: '8px',
                      textAlign: 'center',
                    }}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      id="logo-upload"
                      style={{ display: 'none' }}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const formData = new FormData();
                        formData.append('logo', file);
                        try {
                          const res = await axiosInstance.post('/portal-settings/logo', formData, {
                            headers: { 'Content-Type': 'multipart/form-data' },
                          });
                          if (res.data?.logo_url) {
                            setProfile({ ...profile, logo_url: res.data.logo_url });
                            setSuccessMessage('✅ Logo uploaded');
                            setTimeout(() => setSuccessMessage(null), 3000);
                          }
                        } catch (err: any) {
                          setError(err?.response?.data?.error || 'Logo upload failed');
                        }
                      }}
                    />
                    <label
                      htmlFor="logo-upload"
                      style={{
                        cursor: 'pointer',
                        color: colors.gold,
                        fontSize: '12px',
                        fontWeight: 600,
                      }}
                    >
                      Click to upload brokerage logo
                    </label>
                    <div style={{ fontSize: '10px', color: colors.muted, marginTop: '4px' }}>
                      PNG, JPG, or SVG · Max 5 MB
                    </div>
                  </div>
                )}
              </Field>
            </div>
          </div>
        </SectionCard>

        <SectionCard icon={faPlug} title="Integrations">
          <ComingSoonBlock note="Stripe payment portal, Google Calendar sync, email provider." />
        </SectionCard>

        <SectionCard icon={faBell} title="Notifications">
          <ComingSoonBlock note="Email + in-app notifications for new agents, compliance alerts, billing events." />
        </SectionCard>

        <SectionCard icon={faTriangleExclamation} title="Danger Zone">
          <div style={{ fontSize: '13px', color: colors.muted, marginBottom: '10px' }}>
            Permanent actions. These cannot be undone and are gated behind a confirmation flow in production.
          </div>
          <OutlineButton
            onClick={() => window.alert('Cancel subscription flow lands with Stripe integration')}
            style={{ borderColor: colors.red, color: colors.red }}
          >
            Cancel Subscription
          </OutlineButton>
        </SectionCard>
      </div>
    </div>
  );
};

// ---- Sub-components --------------------------------------------------------

const HeaderCard: React.FC = () => (
  <div
    style={{
      background: colors.card,
      borderLeft: `4px solid ${colors.gold}`,
      borderRadius: '8px',
      padding: '20px 24px',
      marginBottom: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}
  >
    <h1
      style={{
        fontFamily: fonts.display,
        fontSize: '32px',
        color: colors.navy,
        margin: 0,
        letterSpacing: '0.5px',
      }}
    >
      Settings
    </h1>
    <p style={{ color: '#555', fontSize: '14px', margin: '4px 0 0 0' }}>
      Brokerage profile, branding, integrations, and notifications
    </p>
  </div>
);

const SectionCard: React.FC<{ icon: any; title: string; children: React.ReactNode }> = ({
  icon,
  title,
  children,
}) => (
  <div
    style={{
      background: colors.white,
      border: `1px solid ${colors.border}`,
      borderLeft: `4px solid ${colors.gold}`,
      borderRadius: '10px',
      marginBottom: '16px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}
  >
    <div
      style={{
        padding: '14px 20px',
        borderBottom: `1px solid ${colors.border}`,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
      }}
    >
      <FontAwesomeIcon icon={icon} style={{ color: colors.gold, fontSize: '14px' }} />
      <h5
        style={{
          margin: 0,
          fontSize: '11px',
          fontWeight: 700,
          color: colors.navy,
          textTransform: 'uppercase',
          letterSpacing: '1px',
        }}
      >
        {title}
      </h5>
    </div>
    <div style={{ padding: '18px 20px' }}>{children}</div>
  </div>
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label
      style={{
        display: 'block',
        fontSize: '11px',
        fontWeight: 600,
        color: colors.muted,
        marginBottom: '5px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      }}
    >
      {label}
    </label>
    {children}
  </div>
);

const WhiteInput: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => (
  <NavyInput
    {...props}
    style={{
      background: colors.off,
      color: colors.navy,
      border: `1px solid ${colors.border}`,
    }}
  />
);

const ComingSoonBlock: React.FC<{ note: string }> = ({ note }) => (
  <div
    style={{
      background: `${colors.gold}11`,
      border: `1px dashed ${colors.gold}55`,
      borderRadius: '6px',
      padding: '14px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    }}
  >
    <FontAwesomeIcon icon={faCheckCircle} style={{ color: colors.gold2, fontSize: '14px', flexShrink: 0 }} />
    <div style={{ fontSize: '12px', color: '#555', lineHeight: 1.5 }}>
      <strong>Coming soon — </strong>
      {note}
    </div>
  </div>
);

const Alert: React.FC<{ variant: 'success' | 'danger'; children: React.ReactNode }> = ({
  variant,
  children,
}) => (
  <div
    style={{
      background: variant === 'success' ? '#d4edda' : '#f8d7da',
      color: variant === 'success' ? colors.green : colors.red,
      padding: '10px 14px',
      borderRadius: '6px',
      fontSize: '13px',
      fontWeight: 600,
      marginBottom: '16px',
      border: `1px solid ${variant === 'success' ? colors.green : colors.red}44`,
    }}
  >
    {children}
  </div>
);

export default SettingsTab;
